import { extend, find, first, get, isArray, isEmpty, omit, size } from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService } from '@bingads-webui-react/wizard';
import { loadAds, saveAd, deleteAd, saveImageExtensions } from '@bingads-webui-campaign-react/search-campaign-data';
import { AdTypes } from '../../constants/ad-type';

export const useAdsRepository = ({
  enableAdsManagment = false,
  adTypes = [],
} = {}) => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext);
  const {
    cid,
    aid,
    campaignId: campaignIdFromScope,
    adGroupId: adGroupIdFromScope,
  } = scope.values;
  const { state } = useWizardService();
  const {
    campaignId: campaignIdFromWizardData,
    adGroupId: adGroupIdFromWizardData,
    websites: savedWebsites,
  } = state.context.wizardData;

  const campaignId = useMemo(() => campaignIdFromWizardData || campaignIdFromScope, [campaignIdFromScope, campaignIdFromWizardData]);
  const adGroupId = useMemo(() => adGroupIdFromWizardData || adGroupIdFromScope, [adGroupIdFromScope, adGroupIdFromWizardData]);
  const adFilter = useMemo(() => {
    const adTypesFilter = adTypes.map(adType => `Type eq Enum.AdType'${adType}'`).join(' or ');
    return {
      where: `(${adTypesFilter}) and (Status eq Enum.AdStatus'Active')`,
    };
  }, [adTypes]);

  const load = useCallback(() =>
    loadAds({
      cid,
      aid,
      campaignId,
      adGroupId,
      filter: adFilter,
      count: enableAdsManagment ? 100 : 1,
    })
      .then((ads) => {
        if (enableAdsManagment && !isEmpty(ads)) {
          return {
            campaignId,
            adGroupId,
            ads,
            isEmpty: isEmpty(ads),
            loaded: true,
          };
        }

        const data = {
          campaignId,
          adGroupId,
          ads: adTypes.reduce((acc, adType) => {
            acc[adType] = {
              data: {
                FinalUrls: savedWebsites,
              },
              isValid: false,
            };
            return acc;
          }, {}),
          adType: first(adTypes),
          isEmpty: isEmpty(ads),
          loaded: true,
        };

        const ad = first(ads);
        if (ad) {
          extend(data, {
            ads: {
              ...data.ads,
              [ad.Type]: {
                data: ad,
                isValid: true,
              },
            },
            adType: ad.Type,
          });
        }

        return data;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load ads failed: ${error}`);
        activity.signal(`onAdLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);

        return {
          campaignId,
          adGroupId,
          ads: {},
          loaded: true,
          errors: [error],
        };
      }), [activity, adFilter, adGroupId, adTypes, aid, campaignId, cid, enableAdsManagment, savedWebsites]);

  const save = useCallback(({ data }) => {
    const { ads, adType, adExtensions } = data;
    if (isArray(ads)) {
      activity.signal(`onAdsManagementSaving - ${JSON.stringify({
        count: size(ads),
      })}`);

      return Promise.resolve(data);
    }

    const adToSave = ads[adType];
    const adToDelete = !enableAdsManagment && find(ads, (val, key) => key !== adType && get(val, ['data', 'Id']));

    activity.signal(`onAdSaving - ${JSON.stringify({
      adType,
      ad: adToSave.data,
      deletedAd: adToDelete && adToDelete.data,
      extensions: adExtensions,
    })}`);

    const bulkActions = [];
    bulkActions.push(saveAd({
      ad: adToSave.data,
      adType,
      campaignId,
      adGroupId,
      cid,
      aid,
    }, {
      i18n,
      scenario: scenario.name,
      activityLogger: activity,
    })
      .then((response) => {
        const result = {
          ...data,
          ads: {
            ...data.ads,
            [adType]: response,
          },
        };
        if (response.serverErrors || response.systemErrors) {
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            data: result,
            error: {
              serverErrors: response.serverErrors,
              systemErrors: response.systemErrors,
            },
          });
        }
        return Promise.resolve({ data: result });
      }));

    bulkActions.push(adToDelete ? deleteAd({
      adId: adToDelete.data.Id,
      adType: find(adTypes, type => type !== adType),
      adGroupId,
    }, {
      i18n,
      scenario: scenario.name,
      activityLogger: activity,
    }) : Promise.resolve({}));

    bulkActions.push(adType === AdTypes.ResponsiveSearch ? saveImageExtensions({
      campaignId,
      adGroupId,
      adId: adToSave.data.Id,
      imageExtensionChanges: adExtensions,
    }, {
      i18n,
      activityLogger: activity,
      scenario: scenario.name,
      // perfMarker,
    }) : Promise.resolve({}));

    return Promise.allSettled(bulkActions)
      .then((results) => {
        const [{
          status: saveStatus,
          value: savedValue,
          reason: rejectedValue,
        }, {
          status: deleteStatus,
          value: deletedData,
        }] = results;
        const savedResult = savedValue || rejectedValue || {};

        if (deleteStatus === 'fulfilled' && deletedData.data && savedResult.data) {
          const adToUpdate = find(savedResult.data.ads, (val, key) => key !== adType);
          if (adToUpdate) {
            adToUpdate.data = omit(adToUpdate.data, 'Id'); // Prevent duplicate deletion
          }
        }

        if (saveStatus === 'rejected') {
          return Promise.reject(savedResult);
        }

        // Same as legacy workflow, failing to update extensions would not block the ad save
        const savedAd = savedResult.data.ads[adType];
        activity.signal(`onAdSaved - ${JSON.stringify({
          adType,
          ad: savedAd.data,
          deletedAd: adToDelete && adToDelete.data,
          extensions: adExtensions,
          editorialErrors: savedAd.editorialErrors,
        })}`);

        return savedResult.data;
      })
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch((result) => {
        // eslint-disable-next-line no-console
        console.error(`Save ad failed: ${result}`);
        activity.signal(`onAdSaveFailed - ${JSON.stringify({
          adType,
          ad: adToSave.data,
          deletedAd: adToDelete && adToDelete.data,
          extensions: adExtensions,
          error: result,
        })}`);

        return Promise.reject(result);
      });
  }, [activity, adGroupId, adTypes, aid, campaignId, cid, enableAdsManagment, i18n, scenario.name]);

  const destroyAd = useCallback((ad) => {
    activity.signal(`onAdDeleting - ${JSON.stringify({
      ad,
    })}`);

    return deleteAd({
      adId: ad.Id,
      adType: ad.Type,
      adGroupId,
    }, {
      i18n,
      scenario: scenario.name,
      activityLogger: activity,
    })
      .then((response) => {
        activity.signal(`onAdDeleted - ${JSON.stringify({
          ad,
          response,
        })}`);

        return response;
      })
      .catch((result) => {
        // eslint-disable-next-line no-console
        console.error(`Delete ad failed: ${result}`);
        activity.signal(`onAdDeleteFailed - ${JSON.stringify({
          ad,
          error: result,
        })}`);

        return Promise.reject(result);
      });
  }, [activity, adGroupId, i18n, scenario.name]);

  return {
    load,
    save,
    deleteAd: destroyAd,
  };
};
